import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, getAccessToken } from './client.js';

/** RICA verification of an account (API modules/rica, migration 0170). */
export type RicaStatus = 'not_started' | 'submitted' | 'approved' | 'rejected';
export type RicaKind = 'cipc' | 'business_address' | 'id' | 'address';

export interface RicaDocument {
  id: string; kind: RicaKind; file_name: string; content_type: string; bytes: number;
  uploaded_at: string; uploader: string | null;
}
export interface Rica {
  customer_type: 'company' | 'individual';
  status: RicaStatus;
  legal_name: string | null;
  registration_no: string | null;
  submitted_at: string | null;
  reviewed_at: string | null;
  review_note: string | null;
  approved_how: 'review' | 'existing_account' | null;
  reviewer_name: string | null;
  required: Array<{ kind: RicaKind; label: string; hint: string }>;
  documents: RicaDocument[];
  max_bytes: number;
  accepted: string[];
}
export interface RicaReview {
  tenant_id: string; name: string; slug: string; status: RicaStatus; customer_type: 'company' | 'individual';
  legal_name: string | null; submitted_at: string | null; documents: number;
}

const key = (t: string) => ['rica', t] as const;

export function useRica(tenantId: string | null | undefined, enabled = true) {
  return useQuery({
    queryKey: key(tenantId ?? ''),
    queryFn: () => api.get<Rica>(`/v1/tenants/${tenantId}/rica`),
    enabled: !!tenantId && enabled,
  });
}

export function useRicaReviews(enabled = true) {
  return useQuery({
    queryKey: ['rica', 'reviews'],
    queryFn: () => api.get<{ data: RicaReview[] }>('/v1/rica/reviews'),
    enabled,
  });
}

export function useRicaMutations(tenantId: string) {
  const qc = useQueryClient();
  const refresh = () => {
    void qc.invalidateQueries({ queryKey: key(tenantId) });
    void qc.invalidateQueries({ queryKey: ['rica', 'reviews'] });
  };
  const base = `/v1/tenants/${tenantId}/rica`;
  return {
    details: useMutation({
      mutationFn: (b: { customer_type: 'company' | 'individual'; legal_name: string; registration_no: string | null }) =>
        api.put(base, b),
      onSuccess: refresh,
    }),
    /** Multipart, so not through api.ts: the browser sets the boundary itself. */
    upload: useMutation({
      mutationFn: async (v: { kind: RicaKind; file: File }) => {
        const body = new FormData();
        body.append('kind', v.kind);
        body.append('file', v.file);
        const res = await fetch(`/api${base}/documents`, {
          method: 'POST', headers: { Authorization: `Bearer ${getAccessToken() ?? ''}` }, body,
        });
        if (!res.ok) {
          const p = await res.json().catch(() => null) as { detail?: string; title?: string } | null;
          throw new Error(p?.detail ?? p?.title ?? `Upload failed (${res.status})`);
        }
      },
      onSuccess: refresh,
    }),
    remove: useMutation({ mutationFn: (id: string) => api.delete(`${base}/documents/${id}`), onSuccess: refresh }),
    submit: useMutation({ mutationFn: () => api.post(`${base}/submit`, {}), onSuccess: refresh }),
    review: useMutation({
      mutationFn: (b: { decision: 'approve' | 'reject'; note?: string }) => api.post(`${base}/review`, b),
      onSuccess: refresh,
    }),
    /** Withdraw an approval, with the reason the account is told. */
    revoke: useMutation({
      mutationFn: (note: string) => api.post(`${base}/revoke`, { note }),
      onSuccess: refresh,
    }),
  };
}

/** A document opened in a new tab, fetched with the person's token (the file is never at a public address). */
export async function openRicaDocument(tenantId: string, id: string, fileName: string): Promise<void> {
  const res = await fetch(`/api/v1/tenants/${tenantId}/rica/documents/${id}/file`, {
    headers: { Authorization: `Bearer ${getAccessToken() ?? ''}` },
  });
  if (!res.ok) throw new Error(`Could not open the document (${res.status})`);
  const url = URL.createObjectURL(await res.blob());
  const a = document.createElement('a');
  a.href = url; a.download = fileName;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 2_000);
}
